import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Landmark, 
  Plus, 
  Edit2, 
  Trash2, 
  CreditCard,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Loader2,
  ChevronRight,
  Receipt,
  Building2,
  DollarSign,
  Clock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LOAN_TYPES = ['Business Loan', 'Personal Loan', 'Assets Purchase', 'Vehicle Lease'];

export const LoansPage = () => {
  const { stores, selectedStore, setSelectedStore } = useStore();
  const { isAdmin, isSuperUser } = useAuth();
  
  const [selectedStoreId, setSelectedStoreId] = useState('all');
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  
  // Form state
  const [loanForm, setLoanForm] = useState({
    store_id: '',
    lender: '',
    loan_type: '',
    start_date: '',
    final_payment_date: '',
    loan_amount: '',
    interest_rate: '',
    monthly_payment: '',
    payment_day: '1',
    bank_account_id: '',
    comments: ''
  });
  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    bank_reference: '',
    notes: ''
  });
  
  // Interest charge form
  const [interestForm, setInterestForm] = useState({
    charge_date: new Date().toISOString().split('T')[0],
    amount: '',
    notes: ''
  });
  
  // Get bank accounts for selected store
  const storeBankAccounts = useMemo(() => {
    const store = stores.find(s => s.id === loanForm.store_id);
    return store?.bank_accounts || [];
  }, [stores, loanForm.store_id]);
  
  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore]);
  
  useEffect(() => {
    fetchLoans();
    fetchSummary();
  }, [selectedStoreId]);
  
  const fetchLoans = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedStoreId !== 'all') {
        params.store_id = selectedStoreId;
      }
      const response = await axios.get(`${API}/api/loans`, { params });
      setLoans(response.data);
    } catch (error) {
      console.error('Failed to fetch loans:', error);
      toast.error('Failed to load loans');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSummary = async () => {
    try {
      const params = {};
      if (selectedStoreId !== 'all') {
        params.store_id = selectedStoreId;
      }
      const response = await axios.get(`${API}/api/loans/summary`, { params });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };
  
  const fetchLoanDetails = async (loanId) => {
    try {
      const response = await axios.get(`${API}/api/loans/${loanId}`);
      setSelectedLoan(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load loan details');
    }
  };
  
  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    if (storeId !== 'all') {
      const store = stores.find(s => s.id === storeId);
      if (store) {
        setSelectedStore(store);
      }
    }
  };
  
  const openAddLoanDialog = () => {
    setEditingLoan(null);
    setLoanForm({
      store_id: selectedStoreId !== 'all' ? selectedStoreId : '',
      lender: '',
      loan_type: '',
      start_date: '',
      final_payment_date: '',
      loan_amount: '',
      interest_rate: '',
      monthly_payment: '',
      payment_day: '1',
      bank_account_id: '',
      comments: ''
    });
    setLoanDialogOpen(true);
  };
  
  const openEditLoanDialog = (loan) => {
    setEditingLoan(loan);
    setLoanForm({
      store_id: loan.store_id,
      lender: loan.lender,
      loan_type: loan.loan_type,
      start_date: loan.start_date,
      final_payment_date: loan.final_payment_date || '',
      loan_amount: loan.loan_amount.toString(),
      interest_rate: (loan.interest_rate || 0).toString(),
      monthly_payment: loan.monthly_payment.toString(),
      payment_day: (loan.payment_day || 1).toString(),
      bank_account_id: loan.bank_account_id || '',
      comments: loan.comments || ''
    });
    setLoanDialogOpen(true);
  };
  
  const handleSaveLoan = async () => {
    if (!loanForm.store_id || !loanForm.lender || !loanForm.loan_type || 
        !loanForm.start_date || !loanForm.loan_amount || !loanForm.monthly_payment) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      const payload = {
        ...loanForm,
        loan_amount: parseFloat(loanForm.loan_amount),
        interest_rate: parseFloat(loanForm.interest_rate || 0),
        monthly_payment: parseFloat(loanForm.monthly_payment),
        payment_day: parseInt(loanForm.payment_day || 1)
      };
      
      if (editingLoan) {
        await axios.put(`${API}/api/loans/${editingLoan.id}`, payload);
        toast.success('Loan updated successfully');
      } else {
        await axios.post(`${API}/api/loans`, payload);
        toast.success('Loan created successfully');
      }
      
      setLoanDialogOpen(false);
      fetchLoans();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save loan');
    }
  };
  
  const handleDeleteLoan = async (loanId) => {
    if (!window.confirm('Are you sure you want to delete this loan and all its payment history? This cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/loans/${loanId}`);
      toast.success('Loan deleted');
      fetchLoans();
      fetchSummary();
      setDetailDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete loan');
    }
  };
  
  const openPaymentDialog = (loan) => {
    setSelectedLoan(loan);
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: loan.monthly_payment?.toString() || '',
      bank_reference: '',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };
  
  const handleAddPayment = async () => {
    if (!paymentForm.payment_date || !paymentForm.amount) {
      toast.error('Please fill in payment date and amount');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/loans/${selectedLoan.id}/payments`, {
        payment_date: paymentForm.payment_date,
        amount: parseFloat(paymentForm.amount),
        bank_reference: paymentForm.bank_reference,
        notes: paymentForm.notes,
        payment_method: 'manual'
      });
      
      toast.success(`Payment recorded. New balance: £${response.data.new_balance.toFixed(2)}`);
      setPaymentDialogOpen(false);
      
      // Refresh data
      fetchLoans();
      fetchSummary();
      if (detailDialogOpen) {
        fetchLoanDetails(selectedLoan.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };
  
  const handleDeletePayment = async (paymentId, isInterest = false) => {
    const confirmMsg = isInterest 
      ? 'Are you sure you want to delete this interest charge? The loan balance will be reduced.'
      : 'Are you sure you want to delete this payment? The loan balance will be increased.';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    try {
      const endpoint = isInterest 
        ? `${API}/api/loans/interest/${paymentId}`
        : `${API}/api/loans/payments/${paymentId}`;
      
      await axios.delete(endpoint);
      toast.success(isInterest ? 'Interest charge deleted' : 'Payment deleted and balance reversed');
      fetchLoanDetails(selectedLoan.id);
      fetchLoans();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };
  
  const openInterestDialog = (loan) => {
    setSelectedLoan(loan);
    setInterestForm({
      charge_date: new Date().toISOString().split('T')[0],
      amount: '',
      notes: ''
    });
    setInterestDialogOpen(true);
  };
  
  const handleAddInterest = async () => {
    if (!interestForm.charge_date || !interestForm.amount) {
      toast.error('Please fill in date and interest amount');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/api/loans/${selectedLoan.id}/interest`, {
        charge_date: interestForm.charge_date,
        amount: parseFloat(interestForm.amount),
        notes: interestForm.notes || 'Interest charge applied by bank'
      });
      
      toast.success(`Interest charge added. New balance: £${response.data.new_balance.toFixed(2)}`);
      setInterestDialogOpen(false);
      
      // Refresh data
      fetchLoans();
      fetchSummary();
      if (detailDialogOpen) {
        fetchLoanDetails(selectedLoan.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add interest charge');
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };
  
  const getLoanTypeColor = (type) => {
    switch (type) {
      case 'Business Loan': return 'bg-blue-500';
      case 'Personal Loan': return 'bg-purple-500';
      case 'Assets Purchase': return 'bg-amber-500';
      case 'Vehicle Lease': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6" data-testid="loans-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Landmark className="h-6 w-6" />
            Business Loans
          </h1>
          <p className="text-muted-foreground">Manage loans and track payments</p>
        </div>
        
        <div className="flex gap-2">
          <Select value={selectedStoreId} onValueChange={handleStoreChange}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <SelectValue placeholder="Select Store" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button onClick={openAddLoanDialog} data-testid="add-loan-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Loan
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Landmark className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Active Loans</p>
                  <p className="text-2xl font-bold">{summary.active_loans_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/10 rounded-lg">
                  <DollarSign className="h-5 w-5 text-red-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Outstanding</p>
                  <p className="text-2xl font-bold text-red-500">-{formatCurrency(summary.total_outstanding)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg">
                  <Receipt className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Monthly Commitments</p>
                  <p className="text-2xl font-bold">{formatCurrency(summary.monthly_commitments)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${summary.overdue_count > 0 ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                  {summary.overdue_count > 0 ? (
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Overdue Payments</p>
                  <p className={`text-2xl font-bold ${summary.overdue_count > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {summary.overdue_count}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Loans List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Loans ({loans.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : loans.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Landmark className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No loans found</p>
              <p className="text-sm">Click "Add Loan" to create your first loan</p>
            </div>
          ) : (
            <div className="space-y-3">
              {loans.map(loan => (
                <div 
                  key={loan.id}
                  className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors ${
                    loan.is_overdue ? 'border-red-500/50 bg-red-500/5' : 'border-border'
                  }`}
                  onClick={() => fetchLoanDetails(loan.id)}
                  data-testid={`loan-item-${loan.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-12 rounded-full ${getLoanTypeColor(loan.loan_type)}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{loan.lender}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getLoanTypeColor(loan.loan_type)} text-white`}>
                          {loan.loan_type}
                        </span>
                        {loan.status === 'paid_off' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                            Paid Off
                          </span>
                        )}
                        {loan.is_overdue && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            Overdue
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {loan.store_name}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Balance</p>
                      <p className="font-bold text-red-500">-{formatCurrency(loan.current_balance)}</p>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Monthly</p>
                      <p className="font-semibold">{formatCurrency(loan.monthly_payment)}</p>
                    </div>
                    
                    {loan.next_due_date && loan.status === 'active' && (
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Next Due</p>
                        <p className={`font-semibold ${loan.is_overdue ? 'text-red-500' : ''}`}>
                          {format(new Date(loan.next_due_date), 'dd MMM yyyy')}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPaymentDialog(loan);
                        }}
                        disabled={loan.status === 'paid_off'}
                      >
                        <Receipt className="h-4 w-4 mr-1" />
                        Pay
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditLoanDialog(loan);
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Add/Edit Loan Dialog */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              {editingLoan ? 'Edit Loan' : 'Add New Loan'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Store *</Label>
              <Select 
                value={loanForm.store_id} 
                onValueChange={(v) => setLoanForm({ ...loanForm, store_id: v, bank_account_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Store" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Lender *</Label>
              <Input
                value={loanForm.lender}
                onChange={(e) => setLoanForm({ ...loanForm, lender: e.target.value })}
                placeholder="e.g., HSBC, Barclays, Funding Circle"
              />
            </div>
            
            <div>
              <Label>Loan Type *</Label>
              <Select 
                value={loanForm.loan_type} 
                onValueChange={(v) => setLoanForm({ ...loanForm, loan_type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Start Date *</Label>
              <Input
                type="date"
                value={loanForm.start_date}
                onChange={(e) => setLoanForm({ ...loanForm, start_date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Final Payment Date</Label>
              <Input
                type="date"
                value={loanForm.final_payment_date}
                onChange={(e) => setLoanForm({ ...loanForm, final_payment_date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Loan Amount (£) *</Label>
              <Input
                type="number"
                step="0.01"
                value={loanForm.loan_amount}
                onChange={(e) => setLoanForm({ ...loanForm, loan_amount: e.target.value })}
                placeholder="0.00"
                disabled={!!editingLoan}
              />
              {editingLoan && (
                <p className="text-xs text-muted-foreground mt-1">Original amount cannot be changed</p>
              )}
            </div>
            
            <div>
              <Label>Interest Rate (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={loanForm.interest_rate}
                onChange={(e) => setLoanForm({ ...loanForm, interest_rate: e.target.value })}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <Label>Monthly Payment (£) *</Label>
              <Input
                type="number"
                step="0.01"
                value={loanForm.monthly_payment}
                onChange={(e) => setLoanForm({ ...loanForm, monthly_payment: e.target.value })}
                placeholder="0.00"
              />
            </div>
            
            <div>
              <Label>Payment Day of Month *</Label>
              <Select 
                value={loanForm.payment_day} 
                onValueChange={(v) => setLoanForm({ ...loanForm, payment_day: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Day" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                    <SelectItem key={day} value={day.toString()}>
                      {day}{day === 1 ? 'st' : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Bank Account (for reconciliation)</Label>
              <Select 
                value={loanForm.bank_account_id} 
                onValueChange={(v) => setLoanForm({ ...loanForm, bank_account_id: v })}
                disabled={!loanForm.store_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={storeBankAccounts.length === 0 ? "No bank accounts" : "Select Bank"} />
                </SelectTrigger>
                <SelectContent>
                  {storeBankAccounts.map((acc, idx) => (
                    <SelectItem key={acc.id || idx} value={acc.id || `bank_${idx}`}>
                      {acc.bank_name} {acc.account_name ? `- ${acc.account_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="col-span-2">
              <Label>Comments</Label>
              <Textarea
                value={loanForm.comments}
                onChange={(e) => setLoanForm({ ...loanForm, comments: e.target.value })}
                placeholder="Additional notes about the loan..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setLoanDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveLoan}>
              {editingLoan ? 'Update Loan' : 'Create Loan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Loan Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedLoan && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Landmark className="h-5 w-5" />
                    {selectedLoan.lender}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getLoanTypeColor(selectedLoan.loan_type)} text-white`}>
                      {selectedLoan.loan_type}
                    </span>
                  </div>
                  {(isSuperUser || isAdmin) && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      onClick={() => handleDeleteLoan(selectedLoan.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                {/* Loan Info Grid */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
                  <div>
                    <p className="text-xs text-muted-foreground">Store</p>
                    <p className="font-semibold">{selectedLoan.store_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Original Amount</p>
                    <p className="font-semibold">{formatCurrency(selectedLoan.loan_amount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Current Balance</p>
                    <p className="font-semibold text-red-500">-{formatCurrency(selectedLoan.current_balance)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Monthly Payment</p>
                    <p className="font-semibold">{formatCurrency(selectedLoan.monthly_payment)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Interest Rate</p>
                    <p className="font-semibold">{selectedLoan.interest_rate || 0}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Payment Day</p>
                    <p className="font-semibold">{selectedLoan.payment_day || 1}{selectedLoan.payment_day === 1 ? 'st' : selectedLoan.payment_day === 2 ? 'nd' : selectedLoan.payment_day === 3 ? 'rd' : 'th'} of month</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Start Date</p>
                    <p className="font-semibold">{format(new Date(selectedLoan.start_date), 'dd MMM yyyy')}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Next Due Date</p>
                    <p className={`font-semibold ${selectedLoan.is_overdue ? 'text-red-500' : ''}`}>
                      {selectedLoan.next_due_date ? format(new Date(selectedLoan.next_due_date), 'dd MMM yyyy') : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className={`font-semibold ${selectedLoan.status === 'paid_off' ? 'text-green-500' : selectedLoan.status === 'defaulted' ? 'text-red-500' : ''}`}>
                      {selectedLoan.status?.charAt(0).toUpperCase() + selectedLoan.status?.slice(1).replace('_', ' ')}
                    </p>
                  </div>
                </div>
                
                {selectedLoan.comments && (
                  <div className="p-3 bg-amber-500/10 rounded-lg">
                    <p className="text-xs text-muted-foreground mb-1">Comments</p>
                    <p className="text-sm">{selectedLoan.comments}</p>
                  </div>
                )}
                
                {/* Payment Summary */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-green-500/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Paid</p>
                        <p className="text-xl font-bold text-green-500">{formatCurrency(selectedLoan.total_paid || 0)}</p>
                        <p className="text-xs text-muted-foreground">{selectedLoan.payments_count || 0} payments</p>
                      </div>
                      <Button 
                        size="sm"
                        onClick={() => openPaymentDialog(selectedLoan)} 
                        disabled={selectedLoan.status === 'paid_off'}
                      >
                        <Receipt className="h-4 w-4 mr-2" />
                        Record Payment
                      </Button>
                    </div>
                  </div>
                  <div className="p-4 bg-amber-500/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Interest Added</p>
                        <p className="text-xl font-bold text-amber-500">+{formatCurrency(selectedLoan.total_interest || 0)}</p>
                        <p className="text-xs text-muted-foreground">{selectedLoan.interest_count || 0} charges</p>
                      </div>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => openInterestDialog(selectedLoan)} 
                        disabled={selectedLoan.status === 'paid_off'}
                        className="border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Interest
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Transaction History */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Transaction History
                  </h3>
                  {(!selectedLoan.transactions || selectedLoan.transactions.length === 0) ? (
                    <p className="text-center py-6 text-muted-foreground">No transactions recorded yet</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                      {selectedLoan.transactions?.map(txn => {
                        const isInterest = txn.transaction_type === 'interest_charge';
                        return (
                          <div 
                            key={txn.id}
                            className={`flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 ${isInterest ? 'border-amber-500/30' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${isInterest ? 'bg-amber-500/10' : txn.is_reconciled ? 'bg-green-500/10' : 'bg-blue-500/10'}`}>
                                {isInterest ? (
                                  <DollarSign className="h-4 w-4 text-amber-500" />
                                ) : txn.is_reconciled ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Receipt className="h-4 w-4 text-blue-500" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{format(new Date(txn.payment_date), 'dd MMM yyyy')}</p>
                                  {isInterest && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-600">Interest</span>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {isInterest 
                                    ? (txn.notes || 'Interest charge by bank')
                                    : (txn.payment_method === 'bank_reconciliation' ? 'Bank Reconciliation' : 'Manual Entry')
                                  }
                                  {!isInterest && txn.bank_reference && ` • ${txn.bank_reference}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`font-bold ${isInterest ? 'text-amber-500' : 'text-green-500'}`}>
                                {isInterest ? '+' : '-'}{formatCurrency(txn.amount)}
                              </span>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                onClick={() => handleDeletePayment(txn.id, isInterest)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>Close</Button>
                <Button variant="outline" onClick={() => openEditLoanDialog(selectedLoan)}>
                  <Edit2 className="h-4 w-4 mr-2" />
                  Edit Loan
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          
          {selectedLoan && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm"><strong>Loan:</strong> {selectedLoan.lender}</p>
                <p className="text-sm"><strong>Current Balance:</strong> <span className="text-red-500">-{formatCurrency(selectedLoan.current_balance)}</span></p>
                <p className="text-sm"><strong>Expected Monthly:</strong> {formatCurrency(selectedLoan.monthly_payment)}</p>
              </div>
              
              <div>
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <Label>Bank Reference</Label>
                <Input
                  value={paymentForm.bank_reference}
                  onChange={(e) => setPaymentForm({ ...paymentForm, bank_reference: e.target.value })}
                  placeholder="Reference number"
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Interest Charge Dialog */}
      <Dialog open={interestDialogOpen} onOpenChange={setInterestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <DollarSign className="h-5 w-5" />
              Add Interest Charge
            </DialogTitle>
          </DialogHeader>
          
          {selectedLoan && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                <p className="text-sm"><strong>Loan:</strong> {selectedLoan.lender}</p>
                <p className="text-sm"><strong>Current Balance:</strong> <span className="text-red-500">-{formatCurrency(selectedLoan.current_balance)}</span></p>
                <p className="text-xs text-muted-foreground mt-2">
                  Interest charges will <strong>increase</strong> the loan balance.
                </p>
              </div>
              
              <div>
                <Label>Interest Date *</Label>
                <Input
                  type="date"
                  value={interestForm.charge_date}
                  onChange={(e) => setInterestForm({ ...interestForm, charge_date: e.target.value })}
                />
              </div>
              
              <div>
                <Label>Interest Amount (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={interestForm.amount}
                  onChange={(e) => setInterestForm({ ...interestForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={interestForm.notes}
                  onChange={(e) => setInterestForm({ ...interestForm, notes: e.target.value })}
                  placeholder="e.g., Monthly interest charge from bank statement"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setInterestDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddInterest} className="bg-amber-500 hover:bg-amber-600">
              Add Interest Charge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoansPage;
